import { LogOut, Search, Sun, Moon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogoutConfirm } from "@/components/auth/LogoutConfirm";
import { useAuthStore } from "@/lib/auth/store";
import { ROLE_LABELS } from "@/lib/auth/rbac";
import { initials } from "@/lib/utils";

export function Topbar() {
  const session = useAuthStore((s) => s.session);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [dark, setDark] = useState<boolean>(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  function logout() {
    clear();
    navigate("/login", { replace: true });
  }

  const roleLabel = session
    ? ROLE_LABELS[session.role] ?? session.role
    : "";

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/85 backdrop-blur-md px-4 lg:px-6">
      <div className="relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search users, doctors, posts…"
          className="pl-9 bg-muted/50 border-transparent"
        />
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => setDark((d) => !d)}
        aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
        className="ml-auto cursor-pointer"
      >
        {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>

      {session && (
        <>
          {/* Hairline divider keeps the theme toggle visually separate
              from the identity cluster — without it the moon icon
              looked like it belonged to the user info. */}
          <div className="hidden sm:block h-7 w-px bg-border" />

          <div className="flex items-center gap-2.5 pr-1">
            {/* Name + role stack, right-aligned. Tight leading + subtle
                role pill so the cluster reads as a single unit instead
                of two competing labels. */}
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-foreground truncate max-w-[160px]">
                {session.name}
              </span>
              <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                {roleLabel}
              </span>
            </div>

            <Avatar className="size-9 ring-2 ring-background shadow-sm">
              <AvatarFallback>{initials(session.name)}</AvatarFallback>
            </Avatar>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setConfirmOpen(true)}
            aria-label="Sign out"
            className="cursor-pointer"
          >
            <LogOut className="size-4" />
          </Button>

          <LogoutConfirm
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            onConfirm={() => {
              setConfirmOpen(false);
              logout();
            }}
          />
        </>
      )}
    </header>
  );
}
